import React, { useContext, useEffect, useRef, useState } from 'react';

// Third-parties.
import { Dayjs } from 'dayjs';
import * as dayjs from 'dayjs';

import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

// Api.
import api from '../api/Base';

// Context
import { InstanceContext } from '../context/InstanceContext';
import { EventTypeContext } from '../context/EventTypeContext';

// Types.
import { TimeRangeContext, TimeRangeContextType } from "../context/TimeRangeContext";
import { buildApiQuery } from "../helpers/timeRangeUtils";
import InstanceTarget from '../types/instance/InstanceTarget';

// Constants.
import { ACTION_CREATE, ACTION_UPDATE, ACTION_DELETE } from './Constants';

// Components.
import Alert from './Alert';

// Utils.
import { showError } from '../utils/showAlert';

function RegisterEvent(props: { timeRangeContext: TimeRangeContextType }) {
    const { instances } = useContext(InstanceContext);
    const { eventTypes } = useContext(EventTypeContext);
    const timeRangeContext = useContext(TimeRangeContext);
    const modalRef = useRef<HTMLDivElement>(null);

    const [events, setEvents] = useState<any[]>([]);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [isValid, setIsValid] = useState<boolean>(true);
    const [eventId] = useState<number>(0);
    const [title, setTitle] = useState<string>('');
    const [titleUsed, setTitleUsed] = useState<boolean>(false);
    const [titleClass, setTitleClass] = useState<string>('');
    const [eventTypeId, setEventTypeId] = useState(0);
    const [eventTypeIdClass, setEventTypeIdClass] = useState<string>('');
    const [startDateTime, setStartDateTime] = useState<Date>(new Date());
    const [startDateTimeClass, setStartDateTimeClass] = useState<string>('');
    const [endDateTime, setEndDateTime] = useState<Date>(new Date());
    const [endDateTimeClass, setEndDateTimeClass] = useState<string>('');
    const [isPeriod, setIsPeriod] = useState<boolean>(false);
    const [advanced, setAdvanced] = useState<boolean>(false);
    const [colourCode, setColourCode] = useState<string>('#6c757d');
    const [detailsUrl, setDetailsUrl] = useState<string>('');
    const [detailsUrlClass, setDetailsUrlClass] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [filter, setFilter] = useState<string>('');
    const [filterClass, setFilterClass] = useState<string>('');
    const [selectedInstances, setSelectedInstances] = useState<InstanceTarget[]>([]);
    const [mappedInstances, setMappedInstances] = useState<number[]>([]);

    const getChanges = async () => {
        const url = `event?${buildApiQuery(timeRangeContext.timeRange)}&sort=timeslice+desc&limit=50000`;
        const allEvents = await api.get(url).then((response) => response.data);
        setEvents(allEvents);
    };

    const resetForm = () => {
        setIsSuccess(false);
        setIsValid(true);
        setTitle('');
        setTitleClass('');
        setTitleUsed(false);
        setEventTypeId(0);
        setEventTypeIdClass('');
        setFilter('');
        setFilterClass('');
        setMappedInstances([]);
        setSelectedInstances([]);
        setIsPeriod(false);
        setStartDateTime(new Date());
        setStartDateTimeClass('');
        setEndDateTime(new Date());
        setEndDateTimeClass('');
        setAdvanced(false);
        setColourCode('#6c757d');
        setDescription('');
        setDetailsUrl('');
        setDetailsUrlClass('');
    };

    useEffect(() => {
        void getChanges();
    }, []);

    useEffect(() => {
        const el = modalRef.current;
        if (!el) return;
        el.addEventListener('show.bs.modal', resetForm);
        return () => el.removeEventListener('show.bs.modal', resetForm);
    }, []);

    function handleNameBlur() {
        const value = title.trim();
        setTitle(value);

        if (value === '') {
            setTitleClass('is-invalid');
            return;
        }

        setTitleClass('is-valid');

        const index = events.findIndex(object => object.eventId !== eventId && object.title.toLowerCase() === value.toLowerCase());
        if (index === -1) {
            setTitleClass('is-valid');
            setTitleUsed(false);
        } else {
            setTitleClass('is-invalid');
            setTitleUsed(true);
        }
    }

    function handleDetailsUrlBlur(e: { target: { value: string; }; }) {
        if (e.target.value.trim().length === 0) {
            setDetailsUrl('');
            setDetailsUrlClass('');
            return;
        }

        const data = e.target.value.toString();
        const regexp = /^(http|https):\/\/[^ "]+$/;
        setDetailsUrlClass(regexp.test(data) ? 'is-valid' : 'is-invalid');
    }

    function handleInstanceMapping(instanceId: number) {
        let updatedMappedInstances = [...mappedInstances];
        const match = updatedMappedInstances.filter(id => id === instanceId);

        if (match.length === 0) {
            updatedMappedInstances.push(instanceId);
            setSelectedInstances([...selectedInstances, ...instances.filter(instance => instance.id === instanceId)]);
        } else {
            updatedMappedInstances = updatedMappedInstances.filter(id => id !== instanceId);
        }
        setMappedInstances([...updatedMappedInstances]);
    }

    function clearFilter() {
        setFilter('');
    }

    async function handleActionClick() {
        let valid = true;

        if (title.trim() === '') {
            valid = false;
            setTitleClass('is-invalid');
        } else {
            setTitleClass('is-valid');
        }

        if (eventTypeId === 0) {
            valid = false;
            setEventTypeIdClass('is-invalid');
        } else {
            setEventTypeIdClass('is-valid');
        }

        if (mappedInstances.length === 0) {
            valid = false;
            setFilterClass('is-invalid');
        } else {
            setFilterClass('is-valid');
        }

        if (colourCode === '') {
            setColourCode('#6c757d');
        }

        if (isPeriod) {
            const from: Dayjs = dayjs.default(startDateTime);
            const to: Dayjs = dayjs.default(endDateTime);

            if (!from.isValid() || !to.isValid() || from.isAfter(to)) {
                valid = false;
                setStartDateTimeClass('is-invalid');
                setEndDateTimeClass('is-invalid');
            } else {
                setStartDateTimeClass('');
                setEndDateTimeClass('');
            }
        } else {
            setStartDateTimeClass('');
            setEndDateTimeClass('');
        }

        setIsValid(valid);

        if (!valid) {
            return;
        }

        const action = ACTION_CREATE;
        let results: any[] = [];

        try {
            for (let index = 0; index < mappedInstances.length; index++) {
                const data = [{
                    eventId: 0,
                    databaseTargetId: mappedInstances[index],
                    eventTypeId,
                    startDateTime,
                    endDateTime: (isPeriod === true ? endDateTime : null),
                    colourCode,
                    title,
                    description,
                    detailsUrl
                }];

                switch (action) {
                    case ACTION_CREATE:
                        results.push(await api.post(`event?tz=${props.timeRangeContext.timeRange.timezone}`, data));
                        break;
                    case ACTION_UPDATE:
                        results.push(await api.put(`event?tz=${props.timeRangeContext.timeRange.timezone}`, data));
                        break;
                    case ACTION_DELETE:
                        results.push(await api.delete(`event?tz=${props.timeRangeContext.timeRange.timezone}`, { data }));
                        break;
                    default:
                        console.error('Unknown action requested', action);
                        break;
                }
            }

            if (results.filter(result => result.status !== 200).length > 0) {
                console.error('Failed to complete one or more requested actions', action, results);
                return;
            }
        } catch (error: any) {
            console.error('Failed to register event', error);
            const errorMessage = error.response?.data?.message || error.message || error.toString();
            showError('Failed to register event', errorMessage);
            return;
        }

        setIsSuccess(true);
        void getChanges();
    }

    return (
        <React.Fragment>
            <button type="button" className="btn btn-sm btn-success btn-register me-1" data-bs-toggle="modal" data-bs-target="#registerEvent"><i className="fal fa-plus-square fa-fw" /><span>Register Event</span></button>
            <div id="registerEvent" ref={modalRef} className="modal fade text-start" tabIndex={-1} aria-labelledby="registerEventLabel" aria-hidden="true">
                <div className="modal-dialog register-event">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="registerEventLabel">
                                <i className="fal fa-exchange fa-fw" aria-hidden="true" />
                                Register Custom Event
                            </h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="container-fluid">

                                {!isValid && (
                                    <div className="m-2">
                                        <Alert message="Please complete all the required fields below." heading="Missing required fields" variant="alert-danger" />
                                    </div>
                                )}

                                {isSuccess && (
                                    <div className="m-2">
                                        <Alert message="The entered events have been successfully created." heading="Events successfully created" variant="alert-success" />
                                    </div>
                                )}

                                <div className="row form">

                                    <div className="col-12">
                                        <p>Please complete the required details below:</p>
                                    </div>

                                    {/* Title */}
                                    <div className="col-5">
                                        <span className="required"><label htmlFor="name" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="The title you want this event to be listed as">Event Title</label></span>
                                    </div>
                                    <div className="col-7">
                                        <input
                                            type="text"
                                            id="name"
                                            autoFocus
                                            className={`form-control ${titleClass}`}
                                            required
                                            maxLength={50}
                                            onBlur={handleNameBlur}
                                            onChange={e => setTitle(e.target.value)}
                                            value={title}
                                            data-lpignore={true}
                                        />
                                        {titleUsed ? (
                                            <small className="text-warning">Event name already used.</small>
                                        ) : (
                                            <small className="text-secondary">We recommend using a release version.</small>
                                        )}
                                    </div>
                                    <div className="w-100" />

                                    {/* Event Type */}
                                    <div className="col-5">
                                        <span className="required"><label htmlFor="eventTypeId" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="The event type">Event Type</label></span>
                                    </div>
                                    <div className="col-7">
                                        <select
                                            id="eventTypeId"
                                            value={eventTypeId}
                                            onChange={e => setEventTypeId(Number(e.target.value))}
                                            className={`form-select ${eventTypeIdClass}`}>
                                            <option value="0">Select Event Type</option>
                                            {eventTypes.map((eventType) => (
                                                <option key={eventType.eventTypeId} value={eventType.eventTypeId}>{eventType.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="w-100" />

                                    {/* Instances */}
                                    <div className="col-5">
                                        <span className="required"><label htmlFor="mappings" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="Map this custom event to one or more instances">Instances</label></span>
                                        <span className="badge bg-info" data-tooltip-id="global-tooltip" data-tooltip-content="Total mapped instance count">{mappedInstances.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                                    </div>
                                    <div className="col-7">
                                        <div className="row g-0 mb-2">
                                            <div className="col-9">
                                                <input type="text" className={`form-control ${filterClass}`} placeholder="Filter Instances" value={filter} data-lpignore={true} onChange={(e) => setFilter(e.target.value.toLowerCase())} />
                                            </div>
                                            <div className="col-3 text-end">
                                                <button type="button" className="btn btn-small btn-dark" onClick={clearFilter}><i className="fal fa-times" /></button>
                                            </div>
                                        </div>
                                        <div className="list-group">
                                            {instances.filter(item => item.name.toLowerCase().includes(filter)).sort((a, b) => a.name.localeCompare(b.name)).slice(0, 7).map((instance: { id: number; name: string; }) => (
                                                <label key={instance.id} className="list-group-item">
                                                    <input className="form-check-input me-2" type="checkbox" value={instance.id} checked={mappedInstances.filter(id => id === instance.id).length > 0} onChange={() => handleInstanceMapping(instance.id)} />
                                                    {instance.name}
                                                </label>
                                            ))}
                                            {instances.filter(item => item.name.toLowerCase().includes(filter)).length > 7 && (
                                                <label className="list-group-item text-muted">
                                                    <small>Filter for more instances...</small>
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-100" />

                                    {/* Event Period */}
                                    <div className="col-5 d-none">
                                        <label htmlFor="periodEvent" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="Check to set a defined period of time for this event">Event is over a Period</label>
                                    </div>
                                    <div className="col-7 d-none">
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" id="periodEvent" checked={isPeriod} onChange={() => setIsPeriod(v => !v)} />
                                        </div>
                                    </div>
                                    <div className="w-100 d-none" />

                                    {/* Start Date and Time */}
                                    <div className="col-5">
                                        <span className="required"><label htmlFor="startDateTime" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="The event date time">
                                            {isPeriod ? (
                                                <React.Fragment>Start Date</React.Fragment>
                                            ) : (
                                                <React.Fragment>Date and Time</React.Fragment>
                                            )}
                                        </label></span>
                                    </div>
                                    <div className="col-7">
                                        <DatePicker
                                            id="startDateTime"
                                            className={`form-control ${startDateTimeClass}`}
                                            dateFormat="yyyy/MM/dd HH:mm"
                                            placeholderText="yyyy/MM/dd"
                                            popperPlacement="auto"
                                            showTimeSelect
                                            timeFormat="HH:mm"
                                            onChange={(date: Date) => setStartDateTime(date)}
                                            selected={startDateTime}
                                            shouldCloseOnSelect={true} />
                                    </div>
                                    <div className="w-100" />

                                    {isPeriod && (
                                        <React.Fragment>
                                            {/* End Date and Time */}
                                            <div className="col-5">
                                                <span className="required"><label htmlFor="endDateTime" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="The event's end date time">End Date</label></span>
                                            </div>
                                            <div className="col-7">
                                                <DatePicker
                                                    id="endDateTime"
                                                    className={`form-control ${endDateTimeClass}`}
                                                    dateFormat="yyyy/MM/dd HH:mm"
                                                    placeholderText="yyyy/MM/dd"
                                                    popperPlacement="auto"
                                                    minDate={startDateTime}
                                                    showTimeSelect
                                                    timeFormat="HH:mm"
                                                    onChange={(date: Date) => setEndDateTime(date)}
                                                    selected={endDateTime}
                                                    shouldCloseOnSelect={true} />
                                            </div>
                                            <div className="w-100" />
                                        </React.Fragment>
                                    )}

                                    {/* Advanced Mode */}
                                    <div className="col-5">
                                        <label htmlFor="advanced" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="Enable advanced mode">Advanced Mode</label>
                                    </div>
                                    <div className="col-7">
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" id="advanced" checked={advanced} onChange={() => setAdvanced(v => !v)} />
                                        </div>
                                    </div>
                                    <div className="w-100" />

                                    {advanced && (
                                        <React.Fragment>
                                            {isPeriod && (
                                                <React.Fragment>
                                                    {/* Event Colour */}
                                                    <div className="col-5">
                                                        <label htmlFor="colourCode" className="float-start reminder" data-tooltip-id="global-tooltip" data-tooltip-content="Set a custom colour code for this event">Custom Colour Code</label>
                                                        <div className="float-start rounded-pill mt-1 ms-2" style={{ backgroundColor: colourCode, height: '10px', width: '10px' }} />
                                                    </div>
                                                    <div className="col-7">
                                                        <div className="row row-cols-4 g-0">
                                                            <div className="color bg-primary" role="button" onClick={() => setColourCode('#007bff')} />
                                                            <div className="color bg-secondary mx-1" role="button" onClick={() => setColourCode('#6c757d')} />
                                                            <div className="color bg-success" role="button" onClick={() => setColourCode('#28a745')}>&nbsp;</div>
                                                            <div className="color bg-danger mt-1" role="button" onClick={() => setColourCode('#dc3545')} />
                                                            <div className="color bg-warning mt-1 mx-1" role="button" onClick={() => setColourCode('#ffc107')} />
                                                            <div className="color bg-info mt-1" role="button" onClick={() => setColourCode('#17a2b8')}>&nbsp;</div>
                                                        </div>
                                                    </div>
                                                    <div className="w-100" />
                                                </React.Fragment>
                                            )}

                                            {/* Event Description */}
                                            <div className="col-5">
                                                <label htmlFor="description" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="Set the event description">Short Description</label>
                                            </div>
                                            <div className="col-7">
                                                <textarea id="description" className="form-control" value={description} onChange={e => setDescription(e.target.value)} />
                                            </div>
                                            <div className="w-100" />

                                            {/* Release Notes Url */}
                                            <div className="col-5">
                                                <label htmlFor="detailsUrl" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="The URL to your release notes">Release Notes Url</label>
                                            </div>
                                            <div className="col-7">
                                                <input
                                                    type="text"
                                                    id="detailsUrl"
                                                    className={`form-control ${detailsUrlClass}`}
                                                    required
                                                    maxLength={250}
                                                    onBlur={handleDetailsUrlBlur}
                                                    onChange={e => setDetailsUrl(e.target.value)}
                                                    value={detailsUrl}
                                                    data-lpignore={true}
                                                />
                                                {detailsUrlClass !== '' ? (
                                                    <small className="text-danger">Invalid web address entered.</small>
                                                ) : (
                                                    <small className="text-secondary">Optional, set a web link to release notes.</small>
                                                )}
                                            </div>
                                            <div className="w-100" />

                                        </React.Fragment>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer actions">
                            {isSuccess ? (
                                <React.Fragment>
                                    <button className="btn btn-secondary" data-bs-dismiss="modal"><i className="fal fa-arrow-circle-left fa-fw fa-sm" aria-hidden="true" /><span>Close</span></button>
                                    <button className="btn btn-success" onClick={resetForm}><i className="fal fa-plus-square fa-fw fa-sm" /><span>Register Another</span></button>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <button className="btn btn-secondary" data-bs-dismiss="modal"><i className="fal fa-times-circle fa-fw fa-sm" aria-hidden="true" /><span>Cancel</span></button>
                                    <button className="btn btn-success" onClick={handleActionClick}><i className="fal fa-save fa-fw fa-sm" /><span>Register</span></button>
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default RegisterEvent;
